var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { faExclamationTriangle, faInfoCircle, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Context
var ToastContext = createContext(null);
// Hook to use toast
export var useToast = function () {
    var context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
};
var Toast = function (_a) {
    var toast = _a.toast, onClose = _a.onClose;
    var id = toast.id, message = toast.message, type = toast.type, _b = toast.position, _position = _b === void 0 ? "top-right" : _b, _c = toast.duration, duration = _c === void 0 ? 5000 : _c;
    var _d = useState(false), isClosing = _d[0], setIsClosing = _d[1];
    // Handle close with fade-out animation
    var handleClose = useCallback(function () {
        setIsClosing(true);
        // Wait for fade-out animation to complete before removing
        setTimeout(function () {
            onClose(id);
        }, 300); // Match animation duration
    }, [id, onClose]);
    // Auto-dismiss after duration
    useEffect(function () {
        var timer = setTimeout(function () {
            handleClose();
        }, duration);
        return function () { return clearTimeout(timer); };
    }, [duration, handleClose]);
    // Get styles based on type
    var getToastStyles = function () {
        var baseStyles = {
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "12px 16px",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            fontSize: "14px",
            fontWeight: "500",
            maxWidth: "400px",
            minWidth: "300px",
            zIndex: 9999,
            animation: isClosing ? "toastFadeOut 0.3s ease-out" : "toastFadeIn 0.3s ease-out",
            position: "relative",
            marginBottom: "8px",
        };
        switch (type) {
            case "info":
                return __assign(__assign({}, baseStyles), { backgroundColor: "#e3f2fd", color: "#1565c0", border: "1px solid #bbdefb" });
            case "error":
                return __assign(__assign({}, baseStyles), { backgroundColor: "#ffebee", color: "#c62828", border: "1px solid #ffcdd2" });
            default:
                return baseStyles;
        }
    };
    // Get icon based on type
    var getIcon = function () {
        switch (type) {
            case "info":
                return faInfoCircle;
            case "error":
                return faExclamationTriangle;
            default:
                return faInfoCircle;
        }
    };
    return (React.createElement("div", { style: getToastStyles() },
        React.createElement(FontAwesomeIcon, { icon: getIcon() }),
        React.createElement("span", { style: { flex: 1 } }, message),
        React.createElement("button", { onClick: handleClose, style: {
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "3px",
                opacity: 0.7,
                transition: "opacity 0.2s ease",
            }, onMouseEnter: function (e) {
                e.currentTarget.style.opacity = "1";
            }, onMouseLeave: function (e) {
                e.currentTarget.style.opacity = "0.7";
            }, "aria-label": "Close notification" },
            React.createElement(FontAwesomeIcon, { icon: faTimes, size: "sm" }))));
};
var ToastContainer = function (_a) {
    var toasts = _a.toasts, onClose = _a.onClose;
    // Group toasts by position
    var toastsByPosition = toasts.reduce(function (acc, toast) {
        var position = toast.position || "top-right";
        if (!acc[position]) {
            acc[position] = [];
        }
        acc[position].push(toast);
        return acc;
    }, {});
    // Get container styles based on position
    var getContainerStyles = function (position) {
        var baseStyles = {
            position: "fixed",
            zIndex: 9999,
            pointerEvents: "none",
        };
        switch (position) {
            case "top-right":
                return __assign(__assign({}, baseStyles), { top: "20px", right: "20px" });
            case "bottom-center":
                return __assign(__assign({}, baseStyles), { bottom: "20px", left: "50%", transform: "translateX(-50%)" });
            default:
                return baseStyles;
        }
    };
    return (React.createElement(React.Fragment, null, Object.entries(toastsByPosition).map(function (_a) {
        var position = _a[0], positionToasts = _a[1];
        return (React.createElement("div", { key: position, style: getContainerStyles(position) }, positionToasts.map(function (toast) { return (React.createElement("div", { key: toast.id, style: { pointerEvents: "auto" } },
            React.createElement(Toast, { toast: toast, onClose: onClose }))); })));
    })));
};
export var ToastProvider = function (_a) {
    var children = _a.children;
    var _b = useState([]), toasts = _b[0], setToasts = _b[1];
    var showToast = useCallback(function (toast) {
        var id = "toast-".concat(Date.now(), "-").concat(Math.random().toString(36).substr(2, 9));
        var newToast = __assign(__assign({}, toast), { id: id });
        setToasts(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newToast], false); });
    }, []);
    var hideToast = useCallback(function (id) {
        setToasts(function (prev) { return prev.filter(function (toast) { return toast.id !== id; }); });
    }, []);
    var contextValue = {
        showToast: showToast,
        hideToast: hideToast,
    };
    return (React.createElement(ToastContext.Provider, { value: contextValue },
        children,
        createPortal(React.createElement(ToastContainer, { toasts: toasts, onClose: hideToast }), document.body)));
};
export default Toast;
